// Phase 2.5 — REST-backed sessionService.
//
// Mirrors the legacy sessionService surface that lives in firebaseService.js.
// NEW-session auto-promotion to student.memorizedPortions runs server-side now;
// the wirdRatings mirror is deferred to Phase 2.13 when wirdRatings is
// migrated end-to-end. _legacyLogToSession bridging is kept intact (still
// uses dailyLogs for historical tail until 2.13).

import { getAccessToken } from '../contexts/AuthContextV2';

const API_BASE = process.env.REACT_APP_API_URL ?? '';

async function apiFetch(path, options = {}) {
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, { ...options, headers, credentials: 'include' });
  let body = null;
  try { body = await res.json(); } catch { /* non-json */ }
  if (!res.ok) {
    const err = new Error(body?.error || `Request failed: ${res.status}`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

// Match the legacy DEFAULT_QUALITY_RATING shape (each axis 0–5, overall mean).
const DEFAULT_QUALITY_RATING = { accuracy: null, fluency: null, tajweed: null, overall: null };

// ---------------------------------------------------------------------
// Wird mirror — pure spec of the session → wirdRatings projection.
//
// The backend mirrors every saved session into `wirdRatings` rows
// (POST /api/sessions does the write; see al-bayan-backend
// src/routes/sessions.ts) so that the Sijill al-Awrad history and the
// mahfouz mechanism see DailyProgress activity. This helper is the
// canonical, unit-tested description of that mapping — keep the two in
// sync (same dual-copy convention as wirdRules.js ↔ wirdRules.ts):
//
//   • one row per portion in portionsAffected
//   • kind: NEW→'new', OLD→'revision', YESTERDAY_HIFZ→'custom'
//   • hifzScore = accuracy×20, tajweedScore = tajweed×20 (0–5 → 0–100);
//     falls back to overall×20 when the axis is missing
//   • portion.mode = 'custom' + verses when portionVerses[pid] is a
//     non-empty array, else 'rubuu' with empty verses
// ---------------------------------------------------------------------

export const SESSION_TYPE_TO_WIRD_KIND = Object.freeze({
  NEW: 'new',
  OLD: 'revision',
  YESTERDAY_HIFZ: 'custom'
});

export function buildWirdMirrorRows({
  studentId,
  classId = null,
  teacherId = null,
  tenantId = null,
  date,
  type,
  portionsAffected = [],
  portionVerses = {},
  qualityRating = {},
  tajweedRemarks = null,
  audioUrl = null,
  teacherNotes = ''
} = {}) {
  const kind = SESSION_TYPE_TO_WIRD_KIND[type];
  if (!kind || !Array.isArray(portionsAffected) || portionsAffected.length === 0) return [];
  const axisToScore = (v) => (typeof v === 'number' ? Math.round(v * 20) : null);
  const hifzScore = axisToScore(qualityRating?.accuracy) ?? axisToScore(qualityRating?.overall);
  const tajweedScore = axisToScore(qualityRating?.tajweed) ?? axisToScore(qualityRating?.overall);
  return portionsAffected.map((pid) => {
    const hizb = Math.floor((pid - 1) / 4) + 1;
    const rubuu = ((pid - 1) % 4) + 1;
    const verses = Array.isArray(portionVerses?.[pid])
      ? portionVerses[pid]
      : (Array.isArray(portionVerses?.[String(pid)]) ? portionVerses[String(pid)] : []);
    return {
      studentId,
      classId,
      teacherId,
      tenantId,
      date,
      kind,
      portion: verses.length > 0
        ? { mode: 'custom', hizb, rubuu, verses }
        : { mode: 'rubuu', hizb, rubuu, verses: [] },
      hifzScore,
      tajweedScore,
      tajweedRemarks: tajweedRemarks ?? null,
      audioNoteUrl: audioUrl ?? null,
      notes: teacherNotes ?? ''
    };
  });
}

function computeOverallFromAxes(rating) {
  const axes = ['accuracy', 'fluency', 'tajweed'];
  const present = axes.filter((a) => typeof rating[a] === 'number');
  if (present.length === 0) return null;
  return Math.round((present.reduce((s, a) => s + rating[a], 0) / present.length) * 100) / 100;
}

export const sessionService = {
  normalizeQualityRating(rating) {
    const next = { ...DEFAULT_QUALITY_RATING, ...(rating || {}) };
    if (next.overall === null || next.overall === undefined) {
      next.overall = computeOverallFromAxes(next);
    }
    return next;
  },

  async createSession(sessionData) {
    const quality = sessionService.normalizeQualityRating(sessionData.qualityRating);
    const body = {
      studentId: sessionData.studentId,
      classId: sessionData.classId ?? null,
      date: sessionData.date,
      type: sessionData.type,
      portionsAffected: sessionData.portionsAffected ?? [],
      portionVerses: sessionData.portionVerses ?? null,
      qualityRating: quality,
      teacherNotes: sessionData.teacherNotes ?? '',
      attendance: sessionData.attendance ?? 'present',
      mistakes: sessionData.mistakes ?? null,
      tajweedTags: Array.isArray(sessionData.tajweedTags) ? sessionData.tajweedTags : [],
      tajweedRemarks: Array.isArray(sessionData.tajweedRemarks) ? sessionData.tajweedRemarks : null,
      audioUrl: sessionData.audioUrl ?? null
    };
    const { session } = await apiFetch('/api/sessions', { method: 'POST', body: JSON.stringify(body) });
    return session.id;
  },

  async rateSession(sessionId, qualityRating) {
    const quality = sessionService.normalizeQualityRating(qualityRating);
    await apiFetch(`/api/sessions/${encodeURIComponent(sessionId)}`, {
      method: 'PATCH',
      body: JSON.stringify({ qualityRating: quality })
    });
  },

  async updateSession(sessionId, patch) {
    const updates = { ...patch };
    if (patch.qualityRating) updates.qualityRating = sessionService.normalizeQualityRating(patch.qualityRating);
    await apiFetch(`/api/sessions/${encodeURIComponent(sessionId)}`, {
      method: 'PATCH',
      body: JSON.stringify(updates)
    });
  },

  async deleteSession(sessionId) {
    await apiFetch(`/api/sessions/${encodeURIComponent(sessionId)}`, { method: 'DELETE' });
  },

  async getSessionsByStudent(studentId, max = 100) {
    const { sessions } = await apiFetch(`/api/sessions?studentId=${encodeURIComponent(studentId)}&limit=${max}`);
    return sessions;
  },

  async getSessionsByDate(date, classId = null) {
    // The backend matches the `date` column by exact YYYY-MM-DD string, so
    // normalise Date/dayjs inputs before building the query.
    const iso = toIsoDate(date) ?? date;
    const q = classId
      ? `/api/sessions?date=${encodeURIComponent(iso)}&classId=${encodeURIComponent(classId)}`
      : `/api/sessions?date=${encodeURIComponent(iso)}`;
    const { sessions } = await apiFetch(q);
    return sessions;
  },

  // Tenant-wide windowed pull. Used by Director + Teacher dashboards.
  // The backend infers the tenant from the bearer token; staff get a
  // 10k row cap when no studentId is supplied with a date window.
  async getSessionsForTenant({ fromDate, toDate } = {}) {
    const from = toIsoDate(fromDate);
    const to = toIsoDate(toDate);
    if (!from || !to) return [];
    try {
      const { sessions } = await apiFetch(
        `/api/sessions?fromDate=${encodeURIComponent(from)}&toDate=${encodeURIComponent(to)}&order=asc&limit=10000`
      );
      return sessions ?? [];
    } catch (err) {
      console.warn('getSessionsForTenant failed:', err.message);
      return [];
    }
  },

  // Per-student quality time series for the parent dashboard.
  async getQualityTrend(studentId, fromDate, toDate) {
    const from = toIsoDate(fromDate);
    const to = toIsoDate(toDate);
    if (!studentId || !from || !to) return [];
    try {
      const { sessions } = await apiFetch(
        `/api/sessions?studentId=${encodeURIComponent(studentId)}` +
          `&fromDate=${encodeURIComponent(from)}&toDate=${encodeURIComponent(to)}&order=asc&limit=500`
      );
      return (sessions ?? [])
        .map((s) => ({ date: s.date, type: s.type, overall: s?.qualityRating?.overall ?? null }))
        .filter((row) => row.overall !== null);
    } catch (err) {
      console.warn('getQualityTrend failed:', err.message);
      return [];
    }
  },

  // Latest N OLD-session quality scores, oldest -> newest. Used by the
  // Teacher dashboard's red-flag detector.
  async getRecentOldQualities(studentId, count = 3) {
    if (!studentId) return [];
    try {
      const { sessions } = await apiFetch(
        `/api/sessions?studentId=${encodeURIComponent(studentId)}&type=OLD&limit=${Math.max(1, count)}`
      );
      return (sessions ?? [])
        .map((s) => s?.qualityRating?.overall)
        .filter((v) => typeof v === 'number')
        .reverse();
    } catch (err) {
      console.warn('getRecentOldQualities failed:', err.message);
      return [];
    }
  },

  // Most recent NEW session strictly before `beforeDate`. Used by the
  // "load yesterday's NEW" shortcut in the teacher's daily wizard.
  async getLatestNewBefore(studentId, beforeDate) {
    const before = toIsoDate(beforeDate);
    if (!studentId || !before) return null;
    try {
      const { sessions } = await apiFetch(
        `/api/sessions?studentId=${encodeURIComponent(studentId)}&type=NEW&before=${encodeURIComponent(before)}&limit=1`
      );
      return sessions?.[0] ?? null;
    } catch (err) {
      console.warn('getLatestNewBefore failed:', err.message);
      return null;
    }
  }
};

function toIsoDate(d) {
  if (!d) return null;
  if (typeof d === 'string') return d;
  if (typeof d?.toISOString === 'function') return d.toISOString().split('T')[0];
  return null;
}

export default sessionService;
